'use client'

import { Button } from '@qr-front/ui/components/Button'
import { Spinner } from '@qr-front/ui/components/Spinner'
import { cn } from '@qr-front/ui/utils'
import { AxiosError } from 'axios'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { showNotification } from '@/components/Notification'
import { PinField } from '@/components/PinField'

import { getValidErrorMessage } from '../../../utils/apiUtils'

type TOtpCodeFormProps = {
	resendCooldownSeconds: number
	submitLabel: string
	layout?: 'dialog' | 'inline'
	containerId?: string
	onVerify: (code: string) => Promise<void>
	onResend: () => Promise<number>
}

export const OtpCodeForm = ({
	resendCooldownSeconds,
	submitLabel,
	layout = 'dialog',
	containerId,
	onVerify,
	onResend,
}: TOtpCodeFormProps) => {
	const t = useTranslations()
	const [cooldown, setCooldown] = useState(resendCooldownSeconds)
	const [isResending, setIsResending] = useState(false)
	const [pinResetKey, setPinResetKey] = useState(0)

	const form = useForm({
		defaultValues: { code: '' },
		mode: 'onTouched',
	})

	useEffect(() => {
		setCooldown(resendCooldownSeconds)
		form.reset()
		setPinResetKey((k) => k + 1)
	}, [resendCooldownSeconds, form])

	useEffect(() => {
		if (cooldown <= 0) return
		const timer = setTimeout(() => setCooldown((c) => c - 1), 1000)
		return () => clearTimeout(timer)
	}, [cooldown])

	const onSubmitCode = async (values: { code: string }) => {
		try {
			await onVerify(values.code)
		} catch (error) {
			const apiMessage =
				(error instanceof AxiosError &&
					(error.response?.data as { message?: string })?.message) ||
				''
			const isLocked = apiMessage === 'api.errors.login.otpLocked'
			form.setError('code', {
				type: isLocked ? 'locked' : 'error',
				message: getValidErrorMessage(error, t),
			})
			if (isLocked) setCooldown(0)
		}
	}

	const handleResend = async () => {
		try {
			setIsResending(true)
			const newCooldown = await onResend()
			setCooldown(newCooldown)
			form.reset()
			setPinResetKey((k) => k + 1)
		} catch (error) {
			showNotification(getValidErrorMessage(error, t), {
				type: 'error',
				containerId,
			})
		} finally {
			setIsResending(false)
		}
	}

	const isInline = layout === 'inline'
	const hasError = !!form.formState.errors.code

	const resend =
		cooldown > 0 ? (
			<span className="type-body-small text-grey">
				{t('auth__login_otp__resend_cooldown', {
					seconds: cooldown,
				})}
			</span>
		) : (
			<button
				type="button"
				className="type-button-small inline-flex items-center gap-2 text-blue disabled:cursor-not-allowed disabled:opacity-50"
				onClick={handleResend}
				disabled={isResending}
			>
				{t('auth__login_otp__resend')}
				{isResending && <Spinner size={16} />}
			</button>
		)

	return (
		<FormProvider {...form}>
			<form
				className="flex w-full flex-col gap-6"
				onSubmit={form.handleSubmit(onSubmitCode)}
				onKeyUp={(e) => {
					if (
						e.key === 'Enter' &&
						form.getValues('code').length === 6
					) {
						void form.handleSubmit(onSubmitCode)()
					}
				}}
			>
				<div
					className={cn(
						'flex w-full flex-col',
						hasError && !isInline ? 'gap-6' : 'gap-2',
					)}
				>
					<Controller
						name="code"
						render={({ field, fieldState }) => (
							<PinField
								key={pinResetKey}
								showCaret
								inputClassName="!rounded-6 placeholder:font-medium"
								errorClassName="text-center"
								errorMessage={fieldState?.error?.message ?? ''}
								pinInputRootProps={{
									onValueChange: ({ valueAsString }) => {
										field.onChange(valueAsString)
										if (fieldState.error) {
											form.clearErrors('code')
										}
									},
									type: 'numeric',
								}}
							/>
						)}
					/>
					{!isInline && <div className="text-center">{resend}</div>}
				</div>
				<Button
					visual="default"
					size="large"
					className={cn(
						'w-full',
						!isInline && 'tablet:max-w-[350px] tablet:self-center',
						hasError && !isInline && 'bg-grey hover:bg-dark-grey',
					)}
					disabled={hasError || form.formState.isSubmitting}
					type="submit"
				>
					{submitLabel}
					{form.formState.isSubmitting && <Spinner color="white" />}
				</Button>
				{isInline && (
					<div className="type-body-small flex flex-wrap items-center justify-center gap-1 text-center text-dark-grey">
						<span>
							{t('auth__signup_existing_account__resend_prompt')}
						</span>
						{resend}
					</div>
				)}
			</form>
		</FormProvider>
	)
}
